import * as React from 'react';
import { cn } from '@/lib/utils';
import { AppButtonLoader } from '@/modules/shared/components/ui/AppLoading';

/**
 * PremiumButton \u2014 Stripe-style button primitive.
 *
 * Variants:
 *   primary    \u2014 saturated primary CTA (bg-primary)
 *   secondary  \u2014 neutral fill (bg-surface-premium-sunken)
 *   ghost      \u2014 transparent, hover-lift
 *   outline    \u2014 hairline border, transparent
 *   danger     \u2014 destructive CTA
 *
 * Sizes follow \u00a77B Material touch-target rule: h-8 (32px) \u2265 desktop; h-10 (40px)
 * for mobile/`lg`. Use `lg` on mobile-primary actions to respect 48px touch target.
 *
 * Uses `forwardRef` and `asChild` support via inline if slotted later.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium leading-none ' +
  'transition-colors duration-150 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ' +
  'whitespace-nowrap';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 shadow-[0_1px_0_rgba(0,0,0,0.04)]',
  secondary:
    'bg-surface-premium-sunken text-foreground hover:bg-surface-hover active:bg-surface-active border border-border-hairline',
  ghost:
    'bg-transparent text-muted-foreground hover:bg-surface-premium-sunken hover:text-foreground',
  outline:
    'bg-transparent text-foreground border border-border-hairline hover:border-border-premium hover:bg-surface-premium-muted',
  danger:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
};

const SIZE: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[11px]',
  md: 'h-8 px-3 text-[12px]',
  lg: 'h-10 px-4 text-[13px]',
};

export interface PremiumButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Leading icon (auto-sized to 14px) */
  leadingIcon?: React.ReactNode;
  /** Trailing icon (auto-sized to 14px) */
  trailingIcon?: React.ReactNode;
  /** Show a loading indicator and disable the button */
  loading?: boolean;
  /** Stretch to container width */
  fullWidth?: boolean;
}

export const PremiumButton = React.forwardRef<
  HTMLButtonElement,
  PremiumButtonProps
>(
  (
    {
      variant = 'primary',
      size = 'md',
      leadingIcon,
      trailingIcon,
      loading = false,
      fullWidth = false,
      disabled,
      className,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        BASE,
        VARIANT[variant],
        SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading && (
        <AppButtonLoader compact className="bg-current" />
      )}
      {!loading && leadingIcon && (
        <span className="flex-shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5">
          {leadingIcon}
        </span>
      )}
      {children}
      {!loading && trailingIcon && (
        <span className="flex-shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5">
          {trailingIcon}
        </span>
      )}
    </button>
  ),
);
PremiumButton.displayName = 'PremiumButton';
